import { css } from "@/theme";
import { Observation } from "@/resources/types";
import {
  TableSections,
} from "@/components/ObservationChart";
import { getInitialsObsChart, formatUtc2HospitalDate } from "@/global";
import { IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import { useConfirmationModal } from "@imminently/react/context/modals";
import { useNotify } from "@imminently/react/store/notifications";
import { encounterService } from "@/services";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";
import global from "@/global";
import moment from "moment";

const styles = {
  headerRow: css`
    text-align: left;
    b {
      padding-left: 16px;
    }
    & > * {
      text-align: left;
    }
  `,
  headerInitial: css`
    font-weight: bold;
  `,
  buttonContainer: css`
    display: flex;
    gap: 4px;
    justify-content: center;
    margin-top: 4px;
  `,
};

type ObservationChartHeaderRowProps = {
  name: string;
  values: Observation[];
  blankCells?: React.ReactNode[];
  tableSections: TableSections;
  encounterId: string;
  onEditObservation?: (observation: Observation) => void;
};



const ObservationChartHeaderRow = (props: ObservationChartHeaderRowProps) => {
  const { encounterId, onEditObservation } = props;
  const confirmation = useConfirmationModal();
  const notify = useNotify();
  const { settings } = useSettingsContext();
  const { queryClient } = global;

  const subresourceHooks = encounterService.useSubresource(
    "observations",
    encounterId
  );

  const { mutateAsync: updateObservation } = subresourceHooks.useUpdate({
    onSuccess: () => {
      queryClient.refetchQueries(["encounters", encounterId]);
    },
  });

  const hadntLabelsInRange = props.tableSections.some(({ inputs }) =>
    inputs.every(
      (input) => "showOriginValue" in input && input.showOriginValue === true
    )
  );

  const updateWindow = (settings as any)?.encounter?.observations?.updateWindow || 0;

  const isWithinUpdateWindow = (observation: Observation) => {
    if (!updateWindow) return true; // No window restriction if not configured
    if (!observation.created) return false;
    
    const createdTime = moment(observation.created).valueOf();
    const now = moment().valueOf();
    const windowMs = updateWindow * 60 * 1000; // Convert minutes to milliseconds
    
    // Observation is within window if created less than updateWindow minutes ago
    return (now - createdTime) <= windowMs;
  };

  const handleInvalidate = async (observation: Observation) => {
    try {
      await confirmation({
        title: "Mark observation as invalid?",
        message: "This observation will be excluded from risk score calculations. This action can be undone.",
        confirmProps: {
          text: "Mark Invalid",
          color: "warning",
        },
        cancelProps: {
          text: "Cancel",
          variant: "text",
        },
      });

      await updateObservation({
        id: observation.id,
        payload: { invalid: true } as any,
      });
      
      notify.success("Observation marked as invalid");
    } catch (error) {
      // User cancelled or error occurred
      if (error && typeof error === "object" && "message" in error && error.message) {
        // Only show error if it's not a cancellation
        notify.error(error.message as string);
      }
    }
  };

  const handleEdit = (observation: Observation) => {
    if (onEditObservation) {
      onEditObservation(observation);
    }
  };

  return (
    <tr>
      <td css={styles.headerRow} colSpan={hadntLabelsInRange ? 3 : 2}>
        <b>{props.name}</b>
      </td>
      {props.values.map((value, index) => {
        const canEdit = isWithinUpdateWindow(value);
        const displayName = value.actingAsName ?? value.createdByName;
        const tooltipTitle = value.actingAsName
          ? `${displayName} ${formatUtc2HospitalDate(value.lastModified, "DD/MM/YY @ HH:mm")} (signed in as ${value.createdByName})`
          : `${displayName} ${formatUtc2HospitalDate(value.lastModified, "DD/MM/YY @ HH:mm")}`;

        return (
          <td key={index}>
            <Tooltip title={tooltipTitle}>
              <div>
                <div css={styles.headerInitial}>
                  {getInitialsObsChart(displayName)}
                </div>
                <div>{formatUtc2HospitalDate(value.lastModified)}</div>
              </div>
            </Tooltip>
            <div css={styles.buttonContainer}>
              <Tooltip title={canEdit ? "Edit observation" : "Cannot edit outside update window"}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!canEdit}
                    onClick={() => handleEdit(value)}
                    sx={{ padding: "2px", minWidth: "20px", width: "20px", height: "20px" }}
                  >
                    <EditIcon sx={{ fontSize: "14px" }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Mark observation as invalid">
                <IconButton
                  size="small"
                  onClick={() => handleInvalidate(value)}
                  sx={{ padding: "2px", minWidth: "20px", width: "20px", height: "20px" }}
                >
                  <CancelIcon sx={{ fontSize: "14px" }} />
                </IconButton>
              </Tooltip>
            </div>
          </td>
        );
      })}
      {props.blankCells}
    </tr>
  );
};

export default ObservationChartHeaderRow;
